import React from 'react';
import { Image } from 'lucide-react';
import type { Page, Component } from '../ComponentEditModal.types';
import { ColorCustomizationPanel } from '../shared/ColorInput';
import { getColorFieldsForComponent } from '../shared/colorFieldUtils';
import { useCustomColors } from '../../../contexts/CustomColorsContext';
import { RichTextEditor } from '../../RichTextEditor';
import { ButtonsEditor, type ButtonData } from '../shared/ButtonsEditor';
import { MediaPreview, type MediaData } from './MediaTextPreview';
import { hasCropApplied } from '../shared/cropUtils';
import { NumberInput } from '../shared/NumberInput';

interface MediaTextData {
  title?: string;
  subtitle?: string;
  content?: string;
  media?: MediaData;
  layout?: string;
  alignment?: string;
  mediaWidth?: number;
  textWidth?: number;
  spacing?: string;
  titleSize?: string;
  buttons?: ButtonData[];
  customColors?: {
    background?: string;
    title?: string;
    subtitle?: string;
    text?: string;
    buttonBackground?: string;
    buttonText?: string;
  };
}

interface MediaTextEditorProps {
  formData: MediaTextData;
  setFormData: (data: MediaTextData) => void;
  pages: Page[];
  isLoadingPages: boolean;
  onOpenMediaUpload: (mediaType: 'image' | 'video', assetType: string) => void;
  currentPageComponents?: Component[];
}

export const MediaTextEditor: React.FC<MediaTextEditorProps> = ({
  formData,
  setFormData,
  pages,
  isLoadingPages,
  onOpenMediaUpload,
  currentPageComponents,
}) => {
  const data = formData;
  const themeColors = useCustomColors();

  const openMediaUpload = () => {
    onOpenMediaUpload(data.media?.type || 'image', 'media');
  };

  const openThumbnailUpload = () => {
    onOpenMediaUpload('image', 'thumbnail');
  };

  const updateCustomColor = (key: string, value: string | null) => {
    setFormData({
      ...data,
      customColors: { ...data.customColors, [key]: value },
    });
  };

  // Get theme-aware color defaults
  const colorFields = getColorFieldsForComponent('media-text', themeColors);

  return (
    <div className="space-y-8">
      {/* Content Section */}
      <ContentSection data={data} setFormData={setFormData} />

      {/* Media Section */}
      <MediaSection data={data} setFormData={setFormData} openMediaUpload={openMediaUpload} />

      {/* Video Thumbnail Section - only show for uploaded videos */}
      {data.media?.type === 'video' &&
        (!data.media?.sourceType || data.media?.sourceType === 'upload') && (
          <ThumbnailSection
            data={data}
            setFormData={setFormData}
            openThumbnailUpload={openThumbnailUpload}
          />
        )}

      {/* Info message for YouTube/Vimeo - they use their own thumbnails */}
      {data.media?.type === 'video' &&
        (data.media?.sourceType === 'youtube' || data.media?.sourceType === 'vimeo') && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-700">
              {data.media?.sourceType === 'youtube' ? 'YouTube' : 'Vimeo'} videos use their own
              thumbnail. To use a custom thumbnail, upload the video file directly instead.
            </p>
          </div>
        )}

      {/* Layout Section */}
      <LayoutSection data={data} setFormData={setFormData} />

      {/* Styling Section */}
      <StylingSection data={data} setFormData={setFormData} />

      {/* Buttons Section */}
      <ButtonsEditor
        buttons={(data.buttons || []) as ButtonData[]}
        onChange={newButtons => setFormData({ ...data, buttons: newButtons })}
        pages={pages}
        isLoadingPages={isLoadingPages}
        currentPageComponents={currentPageComponents}
        label="Buttons"
        showSize
        showGhostStyle
      />

      {/* Color Customization */}
      <ColorCustomizationPanel
        title="Colors (Optional)"
        description="Customize media-text section colors. Leave empty to use defaults."
        customColors={data.customColors as Record<string, string | undefined>}
        colorFields={colorFields}
        onUpdateColor={updateCustomColor}
        onClearAllColors={() => setFormData({ ...data, customColors: undefined })}
      />
    </div>
  );
};

// Sub-components

interface ContentSectionProps {
  data: MediaTextData;
  setFormData: (data: MediaTextData) => void;
}

const ContentSection: React.FC<ContentSectionProps> = ({ data, setFormData }) => (
  <div>
    <h3 className="text-lg font-medium text-gray-900 mb-4">Content</h3>
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle</label>
        <textarea
          value={data.subtitle || ''}
          onChange={e => setFormData({ ...data, subtitle: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          rows={2}
          placeholder="Enter subtitle (press Enter for line break)"
        />
        <p className="text-xs text-gray-500 mt-1">Press Enter to add a line break</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
        <textarea
          value={data.title || ''}
          onChange={e => setFormData({ ...data, title: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          rows={2}
          placeholder="Enter title (press Enter for line break)"
          required
        />
        <p className="text-xs text-gray-500 mt-1">Press Enter to add a line break</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Content *</label>
        <RichTextEditor
          content={data.content || ''}
          onChange={html => setFormData({ ...data, content: html })}
          placeholder="Enter main content..."
          minHeight="250px"
        />
        <p className="text-xs text-gray-500 mt-1">Use the toolbar to format your text</p>
      </div>
    </div>
  </div>
);

interface MediaSectionProps {
  data: MediaTextData;
  setFormData: (data: MediaTextData) => void;
  openMediaUpload: () => void;
}

const MediaSection: React.FC<MediaSectionProps> = ({ data, setFormData, openMediaUpload }) => {
  const isVideo = data.media?.type === 'video';
  const isCropped = !isVideo && hasCropApplied((data.media as any)?.crop);

  return (
    <div>
      <h3 className="text-lg font-medium text-gray-900 mb-4">Media</h3>
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Media Type</label>
            <select
              value={data.media?.type || 'image'}
              onChange={e => {
                const newType = e.target.value as 'image' | 'video';
                const updatedMedia = { ...data.media, type: newType };
                if (newType === 'video') {
                  delete (updatedMedia as any).crop;
                  if (updatedMedia.borderRadius === 'circle') {
                    updatedMedia.borderRadius = 'large';
                  }
                }
                setFormData({ ...data, media: updatedMedia });
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="image">Image</option>
              <option value="video">Video</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Media Content</label>
            <MediaPreview data={data} setFormData={setFormData} openMediaUpload={openMediaUpload} />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Alt Text / Description
          </label>
          <input
            type="text"
            value={data.media?.alt || ''}
            onChange={e => setFormData({ ...data, media: { ...data.media, alt: e.target.value } })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            placeholder="Describe the media content"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Aspect ratio: images only. Videos use fixed 16:9 for embeds or native ratio for uploads. */}
          {!isVideo && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Aspect Ratio</label>
              <select
                value={isCropped ? 'crop' : data.media?.aspectRatio || 'auto'}
                onChange={e =>
                  setFormData({ ...data, media: { ...data.media, aspectRatio: e.target.value } })
                }
                disabled={isCropped}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 ${isCropped ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isCropped ? (
                  <option value="crop">Set by image crop</option>
                ) : (
                  <>
                    <option value="auto">Auto</option>
                    <option value="16:9">16:9 (Widescreen)</option>
                    <option value="4:3">4:3 (Standard)</option>
                    <option value="1:1">1:1 (Square)</option>
                    <option value="3:2">3:2 (Photo)</option>
                    <option value="9:16">9:16 (Portrait)</option>
                  </>
                )}
              </select>
              {isCropped && (
                <p className="text-xs text-gray-500 mt-1">
                  Aspect ratio is controlled by the image crop. Edit or reset the crop to change it.
                </p>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Border Radius</label>
            <select
              value={
                isVideo && data.media?.borderRadius === 'circle'
                  ? 'large'
                  : data.media?.borderRadius || 'none'
              }
              onChange={e =>
                setFormData({ ...data, media: { ...data.media, borderRadius: e.target.value } })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="none">None</option>
              <option value="small">Small</option>
              <option value="medium">Medium</option>
              <option value="large">Large</option>
              {!isVideo && <option value="circle">Circle</option>}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

interface ThumbnailSectionProps {
  data: MediaTextData;
  setFormData: (data: MediaTextData) => void;
  openThumbnailUpload: () => void;
}

const ThumbnailSection: React.FC<ThumbnailSectionProps> = ({
  data,
  setFormData,
  openThumbnailUpload,
}) => {
  const handleRemoveThumbnail = () => {
    setFormData({
      ...data,
      media: {
        ...data.media,
        thumbnail: undefined,
      },
    });
  };

  const thumbnailUrl = data.media?.thumbnail?.cloudfrontUrl || data.media?.thumbnail?.url;

  return (
    <div>
      <h3 className="text-lg font-medium text-gray-900 mb-4">Video Thumbnail</h3>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Thumbnail Image</label>
          <p className="text-xs text-gray-500 mb-3">
            This image will be displayed before the video plays
          </p>

          {thumbnailUrl ? (
            <div className="space-y-3">
              {/* Thumbnail Preview */}
              <div className="relative border-2 border-gray-200 rounded-lg overflow-hidden bg-gray-50 group inline-block">
                <img
                  src={thumbnailUrl}
                  alt={data.media?.thumbnail?.alt || 'Video thumbnail'}
                  className="w-32 h-24 object-cover"
                />

                {/* Overlay with actions */}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={openThumbnailUpload}
                      className="px-2 py-1 bg-white text-gray-700 rounded shadow text-xs"
                    >
                      Replace
                    </button>
                    <button
                      type="button"
                      onClick={handleRemoveThumbnail}
                      className="px-2 py-1 bg-red-600 text-white rounded shadow text-xs"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div
              className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-primary-400 hover:bg-primary-50 transition-all duration-200 cursor-pointer group"
              onClick={openThumbnailUpload}
            >
              <div className="flex flex-col items-center space-y-2">
                <div className="p-2 bg-gray-100 group-hover:bg-primary-100 rounded-full">
                  <Image size={24} className="text-gray-600 group-hover:text-primary-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Upload Thumbnail</p>
                  <p className="text-xs text-gray-500">PNG, JPG up to 10MB</p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Alt Text</label>
          <input
            type="text"
            value={data.media?.thumbnail?.alt || ''}
            onChange={e =>
              setFormData({
                ...data,
                media: {
                  ...data.media,
                  thumbnail: {
                    url: data.media?.thumbnail?.url || '',
                    ...data.media?.thumbnail,
                    alt: e.target.value,
                  },
                },
              })
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            placeholder="Describe the thumbnail image"
          />
        </div>
      </div>
    </div>
  );
};

interface LayoutSectionProps {
  data: MediaTextData;
  setFormData: (data: MediaTextData) => void;
}

const LayoutSection: React.FC<LayoutSectionProps> = ({ data, setFormData }) => (
  <div>
    <h3 className="text-lg font-medium text-gray-900 mb-4">Layout</h3>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Media Position</label>
        <select
          value={data.layout || 'media-left'}
          onChange={e => setFormData({ ...data, layout: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="media-left">Media Left</option>
          <option value="media-right">Media Right</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Vertical Alignment</label>
        <select
          value={data.alignment || 'center'}
          onChange={e => setFormData({ ...data, alignment: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="top">Top</option>
          <option value="center">Center</option>
          <option value="bottom">Bottom</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Media Width (%)</label>
        <NumberInput
          min={20}
          max={80}
          value={data.mediaWidth || 45}
          fallback={45}
          onChange={mediaWidth => {
            const textWidth = 100 - mediaWidth;
            setFormData({ ...data, mediaWidth, textWidth });
          }}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Text Width (%)</label>
        <NumberInput
          min={20}
          max={80}
          value={data.textWidth || 55}
          fallback={55}
          onChange={textWidth => {
            const mediaWidth = 100 - textWidth;
            setFormData({ ...data, textWidth, mediaWidth });
          }}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        />
      </div>
    </div>
  </div>
);

interface StylingSectionProps {
  data: MediaTextData;
  setFormData: (data: MediaTextData) => void;
}

const StylingSection: React.FC<StylingSectionProps> = ({ data, setFormData }) => (
  <div>
    <h3 className="text-lg font-medium text-gray-900 mb-4">Styling</h3>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Spacing</label>
        <select
          value={data.spacing || 'normal'}
          onChange={e => setFormData({ ...data, spacing: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="compact">Compact</option>
          <option value="normal">Normal</option>
          <option value="spacious">Spacious</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Title Size</label>
        <select
          value={data.titleSize || 'large'}
          onChange={e => setFormData({ ...data, titleSize: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="small">Small</option>
          <option value="medium">Medium</option>
          <option value="large">Large</option>
          <option value="xl">Extra Large</option>
        </select>
      </div>
    </div>
  </div>
);
